import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getMediaPicker,
  getMediaById,
  getMediaProviders,
  updateMediaMetadata,
  deleteMedia,
  bulkDeleteMedia,
  getMediaUsages,
} from "../../services/mediaLibraryService";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { handleErrors } from "../../utils/errorHandler";
import allImages from "../../assets/images-import";
import MediaPreview from "./MediaPreview";
import MediaLibraryPaginationBar from "./MediaLibraryPaginationBar";
import { DEFAULT_MEDIA_PAGE_SIZE } from "./mediaLibraryPagination";
import "./media-library.css";
import "./manage-media-library.css";

const MEDIA_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "document", label: "Document" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!value || Number.isNaN(value)) return "-";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const extractList = (res) => {
  const data = res?.data;
  if (Array.isArray(data)) return data;
  const result = data?.result ?? data?.Result ?? data?.data ?? data?.Data;
  if (Array.isArray(result)) return result;
  return result?.items ?? result?.Items ?? result?.records ?? data?.items ?? [];
};

const extractTotal = (res, fallback) => {
  const data = res?.data;
  const result = data?.result ?? data?.Result ?? data?.data ?? data?.Data ?? {};
  const total =
    result.totalCount ??
    result.total ??
    result.totalRecords ??
    data?.totalCount ??
    data?.total;
  const num = Number(total);
  return Number.isFinite(num) && num > 0 ? num : fallback;
};

const nullSafeText = (value) => (value == null ? "" : String(value));

const normalizeMedia = (raw) => {
  if (!raw) return null;
  return {
    mediaAssetId:
      raw.mediaAssetId ?? raw.id ?? raw._id ?? raw.mediaId ?? raw.assetId,
    mediaUrl: raw.mediaUrl ?? raw.url ?? raw.fileUrl ?? raw.path ?? "",
    thumbnailUrl:
      raw.thumbnailUrl ?? raw.thumbUrl ?? raw.thumbnail ?? raw.mediaUrl ?? raw.url ?? "",
    mediaName: raw.mediaName ?? raw.name ?? raw.fileName ?? raw.title ?? "Untitled",
    providerType: raw.providerType ?? raw.provider ?? raw.source ?? "Upload",
    mediaType: raw.mediaType ?? raw.type ?? "image",
    status: raw.status ?? raw.mediaStatus ?? "",
    width: raw.width ?? raw.imageWidth ?? raw.dimensions?.width ?? null,
    height: raw.height ?? raw.imageHeight ?? raw.dimensions?.height ?? null,
    fileSize: raw.fileSize ?? raw.size ?? raw.sizeInBytes ?? raw.bytes ?? null,
    addedOn:
      raw.addedOn ??
      raw.createdOn ??
      raw.createdAt ??
      raw.uploadedOn ??
      raw.createdDate ??
      "",
    altText: nullSafeText(raw.altText ?? raw.alt_text ?? raw.alt),
    caption: nullSafeText(raw.caption),
    tags: Array.isArray(raw.tags)
      ? raw.tags.join(", ")
      : nullSafeText(raw.tags),
    description: nullSafeText(raw.description),
  };
};

export const ManageMediaLibrary = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_MEDIA_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [keyword, setKeyword] = useState("");
  const [providerType, setProviderType] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [status, setStatus] = useState("");
  const [providerOptions, setProviderOptions] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Modals
  const [previewItem, setPreviewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [metaForm, setMetaForm] = useState({
    altText: "",
    caption: "",
    tags: "",
    description: "",
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [usageItem, setUsageItem] = useState(null);
  const [usages, setUsages] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const fetchMedia = useCallback(
    async (targetPage = 1, overrides = {}) => {
      const size = overrides.pageSize ?? pageSize;
      setLoading(true);
      try {
        const params = {
          keyword: overrides.keyword ?? keyword,
          providerType: overrides.providerType ?? providerType,
          mediaType: overrides.mediaType ?? mediaType,
          status: overrides.status ?? status,
          page: targetPage,
          pageSize: size,
        };
        const res = await getMediaPicker(params);
        const list = extractList(res).map(normalizeMedia).filter(Boolean);
        setItems(list);
        setTotal(extractTotal(res, (targetPage - 1) * size + list.length));
        setPage(targetPage);
        setSelectedIds([]);
      } catch (error) {
        handleErrors(error);
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [keyword, providerType, mediaType, status, pageSize]
  );

  useEffect(() => {
    fetchMedia(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const res = await getMediaProviders();
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : data?.result || data?.data || data?.providers || [];
        const opts = list
          .map((p) =>
            typeof p === "string"
              ? p
              : p?.name || p?.providerType || p?.label || p?.code || ""
          )
          .filter(Boolean);
        setProviderOptions(["Upload", ...opts]);
      } catch {
        setProviderOptions(["Upload"]);
      }
    };
    loadProviders();
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    fetchMedia(1);
  };

  const handleFilterChange = (setter, key) => (value) => {
    setter(value);
    fetchMedia(1, { [key]: value });
  };

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    fetchMedia(1, { pageSize: nextSize });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isUsageConflict = (error, responseData) => {
    const msg = (
      responseData?.message ||
      error?.response?.data?.message ||
      ""
    ).toLowerCase();
    const code = error?.response?.status;
    return (
      code === 409 ||
      msg.includes("used") ||
      msg.includes("usage") ||
      msg.includes("in use") ||
      msg.includes("cannot be deleted")
    );
  };

  const handleDelete = async (item) => {
    const confirmed = await confirmDelete("media asset");
    if (!confirmed) return;
    try {
      const res = await deleteMedia(item.mediaAssetId);
      if (res?.data?.isSuccess === false) {
        if (isUsageConflict(null, res.data)) {
          toast.warn(
            res.data.message ||
              "This media is already used and cannot be deleted."
          );
        } else {
          toast.error(res.data.message || "Delete failed. Please try again.");
        }
        return;
      }
      toast.success("Media deleted successfully.");
      const remaining = items.length - 1;
      fetchMedia(remaining === 0 && page > 1 ? page - 1 : page);
    } catch (error) {
      if (isUsageConflict(error)) {
        toast.warn(
          error?.response?.data?.message ||
            "This media is already used and cannot be deleted."
        );
        return;
      }
      handleErrors(error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = await confirmDelete(
      `${selectedIds.length} selected media asset(s)`
    );
    if (!confirmed) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteMedia({
        ids: selectedIds,
        mediaAssetIds: selectedIds,
      });
      if (res?.data?.isSuccess === false) {
        if (isUsageConflict(null, res.data)) {
          toast.warn(
            res.data.message ||
              "Some media are already used and cannot be deleted."
          );
        } else {
          toast.error(res.data.message || "Bulk delete failed.");
        }
        return;
      }
      toast.success("Selected media deleted successfully.");
      fetchMedia(1);
    } catch (error) {
      if (isUsageConflict(error)) {
        toast.warn(
          error?.response?.data?.message ||
            "Some media are already used and cannot be deleted."
        );
        return;
      }
      handleErrors(error);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openPreview = async (item) => {
    setPreviewItem(item);
    try {
      const detail = await getMediaById(item.mediaAssetId);
      if (detail) setPreviewItem(normalizeMedia(detail));
    } catch {
      /* keep card data as fallback */
    }
  };

  const openEdit = async (item) => {
    setEditItem(item);
    setMetaForm({
      altText: item.altText ?? "",
      caption: item.caption ?? "",
      tags: item.tags ?? "",
      description: item.description ?? "",
    });
    try {
      const detail = await getMediaById(item.mediaAssetId);
      if (detail) {
        const n = normalizeMedia(detail);
        setEditItem(n);
        setMetaForm({
          altText: n.altText ?? "",
          caption: n.caption ?? "",
          tags: n.tags ?? "",
          description: n.description ?? "",
        });
      }
    } catch {
      /* keep card data as fallback */
    }
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setMetaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveMeta = async () => {
    if (!editItem) return;
    setSavingMeta(true);
    try {
      const payload = {
        mediaAssetId: editItem.mediaAssetId,
        id: editItem.mediaAssetId,
        altText: metaForm.altText,
        caption: metaForm.caption,
        tags: metaForm.tags,
        description: metaForm.description,
      };
      const res = await updateMediaMetadata(payload);
      if (res?.data?.isSuccess === false) {
        toast.error(res.data.message || "Update failed. Please try again.");
        return;
      }
      toast.success("Metadata updated successfully.");
      setEditItem(null);
      fetchMedia(page);
    } catch (error) {
      handleErrors(error);
    } finally {
      setSavingMeta(false);
    }
  };

  const openUsage = async (item) => {
    setUsageItem(item);
    setUsages([]);
    setUsageLoading(true);
    try {
      const res = await getMediaUsages(item.mediaAssetId);
      const data = res?.data;
      const list = Array.isArray(data)
        ? data
        : data?.result || data?.data || [];
      setUsages(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
      setUsages([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const renderSkeletonGrid = () => (
    <div className="xc-ml-grid">
      {Array.from({ length: pageSize }).map((_, i) => (
        <div key={i} className="xc-ml-card">
          <div className="xc-ml-card__thumb">
            <div className="xc-skeleton" style={{ width: "100%", height: "100%" }} />
          </div>
          <div className="xc-ml-card__body">
            <div className="xc-skeleton" style={{ width: "70%", height: 12 }} />
            <div className="xc-skeleton" style={{ width: "40%", height: 10 }} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <ComponentHeader title="Media Library" />

      <div className="row">
        <div className="col-12">
          <div className="card mt-xxl-n5">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h5 className="mb-0">Manage Media Assets</h5>
            </div>
            <div className="card-body">
              {/* Filters */}
              <form className="xc-mml-filters" onSubmit={handleSearch}>
                <div className="xc-mml-field">
                  <label>Keyword</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by media name..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <div className="xc-mml-field">
                  <label>Provider Type</label>
                  <select
                    className="form-select"
                    value={providerType}
                    onChange={(e) =>
                      handleFilterChange(setProviderType, "providerType")(
                        e.target.value
                      )
                    }
                  >
                    <option value="">All Providers</option>
                    {providerOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="xc-mml-field">
                  <label>Media Type</label>
                  <select
                    className="form-select"
                    value={mediaType}
                    onChange={(e) =>
                      handleFilterChange(setMediaType, "mediaType")(
                        e.target.value
                      )
                    }
                  >
                    {MEDIA_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="xc-mml-field">
                  <label>Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) =>
                      handleFilterChange(setStatus, "status")(e.target.value)
                    }
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="xc-mml-field">
                  <button type="submit" className="btn btn-primary w-100">
                    <i className="ri-search-line me-1" aria-hidden="true" />
                    Search
                  </button>
                </div>
              </form>

              {/* Toolbar */}
              <div className="xc-mml-toolbar">
                <span className="xc-mml-toolbar__count">
                  {loading
                    ? "Loading..."
                    : `${total || items.length} asset(s)${
                        selectedIds.length
                          ? ` • ${selectedIds.length} selected`
                          : ""
                      }`}
                </span>
                <span className="xc-mml-toolbar__spacer" />
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={selectedIds.length === 0 || bulkDeleting}
                  onClick={handleBulkDelete}
                >
                  <i className="ri-delete-bin-6-line me-1" aria-hidden="true" />
                  {bulkDeleting
                    ? "Deleting..."
                    : `Bulk Delete${
                        selectedIds.length ? ` (${selectedIds.length})` : ""
                      }`}
                </button>
              </div>

              {/* Grid */}
              {loading ? (
                renderSkeletonGrid()
              ) : items.length === 0 ? (
                <div className="xc-ml-empty">
                  <i className="ri-image-2-line" aria-hidden="true" />
                  <span className="xc-ml-empty__title">No media found</span>
                  <span className="xc-ml-empty__text">
                    Try adjusting your search or filters.
                  </span>
                </div>
              ) : (
                <div className="xc-ml-grid">
                  {items.map((item) => {
                    const selected = selectedIds.includes(item.mediaAssetId);
                    return (
                      <div
                        key={item.mediaAssetId || item.mediaUrl}
                        className={`xc-ml-card xc-mml-card ${
                          selected ? "is-selected" : ""
                        }`}
                      >
                        <label className="xc-mml-check">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleSelect(item.mediaAssetId)}
                          />
                        </label>

                        <div className="xc-ml-card__thumb">
                          {item.providerType ? (
                            <span className="xc-ml-card__badge">
                              <i className="ri-image-line" aria-hidden="true" />
                              {item.providerType}
                            </span>
                          ) : null}
                          <MediaPreview
                            src={item.thumbnailUrl || item.mediaUrl}
                            alt={item.mediaName}
                          />
                        </div>

                        <div className="xc-ml-card__body">
                          <p className="xc-ml-card__name" title={item.mediaName}>
                            {item.mediaName}
                          </p>
                          <div className="xc-ml-card__meta">
                            {item.width && item.height ? (
                              <span>
                                <i className="ri-aspect-ratio-line" aria-hidden="true" />
                                {item.width} × {item.height}
                              </span>
                            ) : null}
                            <span>
                              <i className="ri-hard-drive-2-line" aria-hidden="true" />
                              {formatBytes(item.fileSize)}
                            </span>
                          </div>
                          <span className="xc-mml-card__date">
                            <i className="ri-calendar-line me-1" aria-hidden="true" />
                            {formatDate(item.addedOn)}
                          </span>

                          <div className="xc-mml-actions">
                            <button
                              type="button"
                              className="xc-mml-action"
                              title="Preview"
                              onClick={() => openPreview(item)}
                            >
                              <i className="ri-eye-line" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="xc-mml-action"
                              title="Edit Metadata"
                              onClick={() => openEdit(item)}
                            >
                              <i className="ri-edit-2-line" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="xc-mml-action"
                              title="View Usage"
                              onClick={() => openUsage(item)}
                            >
                              <i className="ri-links-line" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="xc-mml-action xc-mml-action--danger"
                              title="Delete"
                              onClick={() => handleDelete(item)}
                            >
                              <i className="ri-delete-bin-6-line" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination + page size — via MediaLibraryPaginationBar */}
              <MediaLibraryPaginationBar
                currentPage={page}
                totalPages={totalPages}
                totalEntries={total}
                pageSize={pageSize}
                onPageChange={(p) => fetchMedia(p)}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        show={Boolean(previewItem)}
        onHide={() => setPreviewItem(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <h5 className="xc-ml-header__title">
            <i className="ri-eye-line" aria-hidden="true" />
            Media Preview
          </h5>
        </Modal.Header>
        <Modal.Body>
          {previewItem ? (
            <div className="row g-3">
              <div className="col-md-7">
                <img
                  src={previewItem.mediaUrl || previewItem.thumbnailUrl}
                  alt={previewItem.mediaName}
                  className="xc-mml-preview-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = allImages.DefultImage;
                  }}
                />
              </div>
              <div className="col-md-5">
                <ul className="xc-mml-detail-list">
                  <li>
                    <span>Name</span>
                    <span>{previewItem.mediaName}</span>
                  </li>
                  <li>
                    <span>Provider</span>
                    <span>{previewItem.providerType || "-"}</span>
                  </li>
                  <li>
                    <span>Dimensions</span>
                    <span>
                      {previewItem.width && previewItem.height
                        ? `${previewItem.width} × ${previewItem.height}px`
                        : "-"}
                    </span>
                  </li>
                  <li>
                    <span>File Size</span>
                    <span>{formatBytes(previewItem.fileSize)}</span>
                  </li>
                  <li>
                    <span>Created Date</span>
                    <span>{formatDate(previewItem.addedOn)}</span>
                  </li>
                </ul>
              </div>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      {/* Edit Metadata Modal */}
      <Modal
        show={Boolean(editItem)}
        onHide={() => (savingMeta ? null : setEditItem(null))}
        centered
      >
        <Modal.Header closeButton>
          <h5 className="xc-ml-header__title">
            <i className="ri-edit-2-line" aria-hidden="true" />
            Edit Metadata
          </h5>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Alt Text</label>
            <input
              type="text"
              name="altText"
              className="form-control"
              value={metaForm.altText}
              onChange={handleMetaChange}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Caption</label>
            <input
              type="text"
              name="caption"
              className="form-control"
              value={metaForm.caption}
              onChange={handleMetaChange}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Tags</label>
            <input
              type="text"
              name="tags"
              className="form-control"
              placeholder="Comma separated tags"
              value={metaForm.tags}
              onChange={handleMetaChange}
            />
          </div>
          <div className="mb-0">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              rows="3"
              className="form-control"
              value={metaForm.description}
              onChange={handleMetaChange}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setEditItem(null)}
            disabled={savingMeta}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveMeta}
            disabled={savingMeta}
          >
            {savingMeta ? "Saving..." : "Save Changes"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* View Usage Modal */}
      <Modal
        show={Boolean(usageItem)}
        onHide={() => setUsageItem(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <h5 className="xc-ml-header__title">
            <i className="ri-links-line" aria-hidden="true" />
            Media Usage
          </h5>
        </Modal.Header>
        <Modal.Body>
          {usageLoading ? (
            <div className="text-center text-muted py-4">Loading usages...</div>
          ) : usages.length === 0 ? (
            <div className="xc-ml-empty">
              <i className="ri-links-line" aria-hidden="true" />
              <span className="xc-ml-empty__title">Not used anywhere</span>
              <span className="xc-ml-empty__text">
                This media asset is not currently referenced by any entity.
              </span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="xc-mml-usage-table">
                <thead>
                  <tr>
                    <th>Entity Type</th>
                    <th>Entity Id</th>
                    <th>Usage Context</th>
                    <th>Added On</th>
                  </tr>
                </thead>
                <tbody>
                  {usages.map((u, i) => (
                    <tr key={u.id ?? u.usageId ?? i}>
                      <td>{u.entityType || u.EntityType || "-"}</td>
                      <td>{u.entityId ?? u.EntityId ?? "-"}</td>
                      <td>{u.usageContext || u.UsageContext || u.context || "-"}</td>
                      <td>
                        {formatDate(
                          u.addedOn || u.createdOn || u.createdAt || u.AddedOn
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ManageMediaLibrary;
